import { isAxiosError } from 'axios';
import { getAxiosClient } from './common';

const axios = getAxiosClient();

// ---------------------------------------------------------------------------
// Org layer config — client API.
//
// Membership-only sibling of api/layerConfig.ts: the org document stores which
// leaf ids are the org's default set, nothing else. Types are a deliberate
// client-side duplicate (the owning modules reach @civilgrid/db at module load and
// cannot be bundled), validated server-side on every read/write.
// ---------------------------------------------------------------------------

/** Per-leaf entry in the org document. `missingSince` is server-owned; the client never writes it. */
export interface OrgLayerNodeEntry {
  missingSince?: string;
}

/** The whole org document — presence of a leaf id = membership in the org default set. */
export interface OrgLayerConfigDocument {
  nodes: Record<string, OrgLayerNodeEntry>;
}

/**
 * Outcome of `GET /v1/orgLayerConfig`. UNLIKE the user endpoint, `not_seeded` carries a real
 * `document` (the server's computed CivilGrid defaults, not yet persisted) — the caller must
 * use it, not discard it. `error` (denial / 5xx / network / unexpected 2xx shape) must never be
 * treated as a document.
 */
export type OrgLayerConfigGetResult =
  | { status: 'found'; document: OrgLayerConfigDocument }
  | { status: 'not_seeded'; document: OrgLayerConfigDocument }
  | { status: 'error'; httpStatus?: number };

/** Wire shape of a 200 from `GET /v1/orgLayerConfig` (server: `controllers/orgLayerConfig.ts`). */
type OrgLayerConfigGetResponse =
  | { status: 'found'; document: OrgLayerConfigDocument }
  | { status: 'not_seeded'; document: OrgLayerConfigDocument };

const ORG_LAYER_CONFIG_PATH = '/v1/orgLayerConfig';

/**
 * `GET /v1/orgLayerConfig?organizationId=`. Returns a discriminated result rather than throwing.
 * Both `found` and `not_seeded` carry a usable document; an unexpected 2xx body is `error`.
 */
export async function getOrgLayerConfig(organizationId: string): Promise<OrgLayerConfigGetResult> {
  try {
    const { data } = await axios.get<OrgLayerConfigGetResponse>(ORG_LAYER_CONFIG_PATH, {
      params: { organizationId },
    });
    if (data?.status === 'found' && data.document) return { status: 'found', document: data.document };
    if (data?.status === 'not_seeded' && data.document) return { status: 'not_seeded', document: data.document };
    return { status: 'error' };
  } catch (e) {
    return { status: 'error', httpStatus: isAxiosError(e) ? e.response?.status : undefined };
  }
}

/**
 * `PUT /v1/orgLayerConfig?organizationId=` (Owner-only, server-enforced). Full-document replace,
 * last-write-wins. Backs the debounced sync; the caller gates every call on `canPersist`.
 * Rejects on failure; the caller retains dirty and retries on the next flush.
 */
export async function putOrgLayerConfig(organizationId: string, document: OrgLayerConfigDocument): Promise<void> {
  await axios.put(ORG_LAYER_CONFIG_PATH, document, { params: { organizationId } });
}
